import { Box, CircularProgress } from '@mui/material';
import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router-dom';

import { CookieConsentBanner } from './components/cookieConsent/CookieConsentBanner';
import ErrorPage from './components/errorPage/ErrorPage';
import Footer from './components/footer/Footer';
import Header from './components/header/components/Header';
import ThemeEnum from './components/header/components/layout/shared/themeSelector/themeEnum';
import { AppTheme } from './components/header/components/layout/Theme';
import AuthenticatedLayout from './components/layout/AuthenticatedLayout';
import RootLayout from './components/layout/RootLayout';
import { SessionExpiryModal } from './components/session/SessionExpiryModal';
import SignOut from './components/signOut/signOut';
import SignUpPage from './components/signup/SignUpPage';
import { ActivePracticeProvider } from './context/ActivePracticeContext';
import { AuthProvider } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import { LicenseProvider } from './context/LicenseContext';
import { PracticeProvider } from './context/PracticeContext';
import LicenseGuard from './guards/LicenseGuard';
import RoleGuard from './guards/RoleGuard';
import SubscriptionGuard from './guards/SubscriptionGuard';
import { UserRole } from './types/user';
import isAuthenticated from './utils/authUtils';
import { getCookie } from './utils/cookie';

// Static imports for small/critical components (needed immediately)

// Lazy-loaded route components (code-split for better initial load)
const Home = lazy(() => import('./components/home/Home'));
const About = lazy(() => import('./components/about/About'));
const Blog = lazy(() => import('./components/blog/Blog'));
const Services = lazy(() => import('./components/services/Services'));
const ContactUs = lazy(() => import('./components/contact/Contact'));
const PricingPage = lazy(() => import('./components/marketing/PricingPage'));
const SecurityPage = lazy(() => import('./components/marketing/SecurityPage'));
const TermsOfServicePage = lazy(() => import('./components/marketing/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./components/marketing/PrivacyPolicyPage'));
const SplashPage = lazy(() => import('./components/subscription/SplashPage'));
const SubscriptionManagementPage = lazy(
  () => import('./components/subscription/SubscriptionManagementPage')
);
const SubscriptionSuccess = lazy(() => import('./components/subscription/SubscriptionSuccess'));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const ClientDashboard = lazy(() => import('./components/dashboard/ClientDashboard'));
const ClientManagement = lazy(() => import('./components/clientManagement/ClientManagement'));
const ClientProfilePage = lazy(() => import('./pages/ClientProfilePage'));
const SystemAdminAccountLookupPage = lazy(() => import('./pages/SystemAdminAccountLookupPage'));
const SystemAdminPracticesPage = lazy(() => import('./pages/SystemAdminPracticesPage'));
const SystemAdminPracticeDetailPage = lazy(() => import('./pages/SystemAdminPracticeDetailPage'));
const SystemAdminAuditLogPage = lazy(() => import('./pages/SystemAdminAuditLogPage'));
const MfaSetupPage = lazy(() => import('./pages/MfaSetupPage'));
const ManageUsers = lazy(() => import('./components/account/AccountManagement'));
const ScheduleComponent = lazy(() => import('./components/calendar/Schedule'));
const SchedulerComponent = lazy(() => import('./components/scheduler/Schedule'));
const Messaging = lazy(() => import('./components/chat/Messaging'));
const OnboardingFlow = lazy(() => import('./components/onboarding/OnboardingFlow'));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage'));
const WhoopCallbackHandler = lazy(() => import('./pages/WhoopCallbackHandler'));
const ZoomCallbackHandler = lazy(() => import('./pages/ZoomCallbackHandler'));
const GoogleCallbackHandler = lazy(() => import('./pages/GoogleCallbackHandler'));
const FitbitCallbackHandler = lazy(() => import('./pages/FitbitCallbackHandler'));
const TrainingPage = lazy(() => import('./pages/TrainingPage'));
const FormsPage = lazy(() => import('./pages/FormsPage'));
const MyFormsPage = lazy(() => import('./components/forms/MyFormsPage'));
const MyFormDetailPage = lazy(() => import('./components/forms/MyFormDetailPage'));
const RecipesPage = lazy(() => import('./pages/RecipesPage'));
const MealPlansPage = lazy(() => import('./pages/MealPlansPage'));
const MyMealPlansPage = lazy(() => import('./components/mealplans/MyMealPlansPage'));
const MyMealPlanDetailPage = lazy(() => import('./components/mealplans/MyMealPlanDetailPage'));
const MyProgramsPage = lazy(() => import('./components/training/MyProgramsPage'));
const MyProgramDetailPage = lazy(() => import('./components/training/MyProgramDetailPage'));
const HealthMetricsPage = lazy(() => import('./components/healthMetrics/HealthMetricsPage'));
const PatientAppointmentsPage = lazy(
  () => import('./components/appointments/PatientAppointmentsPage')
);
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const GroupSessionsPage = lazy(() => import('./pages/GroupSessionsPage'));
const TelehealthPage = lazy(() => import('./pages/TelehealthPage'));
const BroadcastsPage = lazy(() => import('./pages/BroadcastsPage'));
const TeamCalendarPage = lazy(() => import('./pages/TeamCalendarPage'));
// HA-237: Orphaned component UI integration (lazy-loaded)
const Forum = lazy(() => import('./components/forum/Forum'));
// HelpCenter is now a global drawer - no longer needs a dedicated route
const ExerciseBrowser = lazy(() => import('./components/exercises/ExerciseBrowser'));
// HA-470: a practitioner's custom exercises (create/edit/delete, with image and video upload)
const MyExercises = lazy(() => import('./components/exercises/MyExercises'));
// RecipeList is now rendered inside RecipesPage — no direct route needed
const ResourceLibrary = lazy(() => import('./components/resources/ResourceLibrary'));
const TemplateLibrary = lazy(() => import('./components/templates/TemplateLibrary'));

// Loading fallback component for lazy-loaded routes
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100%',
    }}
  >
    <CircularProgress />
  </Box>
);

// Suspense wrapper for lazy-loaded components
const LazyLoad = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
);

/**
 * LoginRedirect - Redirects /login to /LogIn preserving query parameters (HA-226)
 * This ensures API interceptors using lowercase /login work correctly with the
 * router which uses /LogIn, while preserving session_expired and other params.
 */
const LoginRedirect: React.FC = () => {
  const location = useLocation();

  // Preserve query string (e.g., ?reason=session_expired) when redirecting
  return <Navigate to={`/LogIn${location.search}`} replace />;
};

/**
 * ScheduleRedirect - Redirects /Schedule to /Scheduling preserving query
 * parameters (HA-321). Defensive catch for stale bookmarks and any link
 * sites that pre-date the HA-321 rename. The canonical route is
 * /Scheduling (declared below); legitimate links now navigate there
 * directly. This redirect exists so that historic URLs (e.g. the
 * /Schedule?clientIds=...&mode=group pattern surfaced 2026-05-13) keep
 * working without 404ing the user.
 */
const ScheduleRedirect: React.FC = () => {
  const location = useLocation();

  return <Navigate to={`/Scheduling${location.search}`} replace />;
};

/**
 * BroadcastRedirect - Redirects /Broadcast to /broadcasts preserving
 * query parameters (HA-326). Mirror of ScheduleRedirect (HA-321) /
 * LoginRedirect (HA-226). The canonical route is /broadcasts (declared
 * below); legitimate links now navigate there directly. This redirect
 * catches stale bookmarks like /Broadcast?clientIds=... that pre-date
 * the link rename in clientManagement components.
 */
const BroadcastRedirect: React.FC = () => {
  const location = useLocation();

  return <Navigate to={`/broadcasts${location.search}`} replace />;
};

const App = () => {
  let themeCookie = getCookie('theme');

  if (themeCookie === '') {
    themeCookie = `${ThemeEnum.lightTheme}`;
  }
  const getCurrentTheme = parseInt(themeCookie ?? '0');

  // HA-233: Use flex column layout for proper sticky footer positioning
  // The Fragment was providing no layout control, causing Footer to appear mid-page
  // Content div must also be flex to properly pass flex context to children (Dashboard)
  const getWrappedElement = (element: React.ReactNode) => (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{element}</div>
      <Footer />
    </div>
  );

  const getErrorElement = () => (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <ErrorPage />
      </div>
      <Footer />
    </div>
  );

  const createRoute = (
    path: string,
    element: React.ReactNode,
    isProtected: boolean = false,
    requiresSubscription: boolean = false,
    errorElement?: React.ReactNode,
    skipShell: boolean = false
  ) => {
    // Basic route structure
    const route = {
      path,
      errorElement: errorElement || getErrorElement(),
    };

    // Check authentication first
    if (isProtected && !isAuthenticated()) {
      return {
        ...route,
        element: <Navigate to="/LogIn" />,
      };
    }

    // If this route requires subscription check
    if (requiresSubscription) {
      return {
        ...route,
        element: (
          <SubscriptionGuard redirectTo="/subscription/splash">
            {skipShell ? element : getWrappedElement(element)}
          </SubscriptionGuard>
        ),
      };
    }

    // Regular route
    return {
      ...route,
      element: skipShell ? element : getWrappedElement(element),
    };
  };

  /**
   * Create a route that renders inside the AuthenticatedLayout (sidebar shell).
   * The layout already provides Header, Footer, sidebar, and mobile nav.
   * Content is rendered via <Outlet />.
   */
  const createAuthRoute = (
    path: string,
    element: React.ReactNode,
    requiresSubscription: boolean = true,
    errorElement?: React.ReactNode
  ) => {
    const route = {
      path,
      errorElement: errorElement || getErrorElement(),
    };

    if (!isAuthenticated()) {
      return {
        ...route,
        element: <Navigate to="/LogIn" />,
      };
    }

    if (requiresSubscription) {
      return {
        ...route,
        element: <SubscriptionGuard redirectTo="/subscription/splash">{element}</SubscriptionGuard>,
      };
    }

    return {
      ...route,
      element,
    };
  };

  /**
   * HA-259: wrap a route's element in a RoleGuard that redirects Client
   * users to /Dashboard. Used for practitioner-only routes (training,
   * forms, recipes, meal plans, templates, resources, reports,
   * exercises, broadcasts, etc.) so a Client typing the URL ends up on
   * their dashboard instead of in practitioner UI or an "Oops" error.
   */
  const practitionerOnly = (element: React.ReactNode): React.ReactNode => (
    <RoleGuard allow={[UserRole.Practitioner, UserRole.PracticeAdmin, UserRole.SystemAdmin]}>
      {element}
    </RoleGuard>
  );

  // HA-351: keep non-Client roles off patient-only surfaces (e.g. /health-metrics,
  // which calls the patient daily-logs endpoints). A Practitioner/PracticeAdmin
  // typing the URL is redirected to /Dashboard instead of hitting a forbidden,
  // 404-ing client-scoped call. Clients still reach it via the patient nav.
  const clientOnly = (element: React.ReactNode): React.ReactNode => (
    <RoleGuard allow={[UserRole.Client]}>{element}</RoleGuard>
  );

  // HA-411: SystemAdmin-only. Read-only account lookup tool - not (yet)
  // open to any narrower "Support" role, per the ticket's own scoping
  // decision (SupportStaff wiring is explicitly out of scope for v1).
  const systemAdminOnly = (element: React.ReactNode): React.ReactNode => (
    <RoleGuard allow={[UserRole.SystemAdmin]}>{element}</RoleGuard>
  );

  const router = createBrowserRouter(
    [
      {
        // Root layout wraps all routes - provides UpgradePromptProvider, SubscriptionProvider, etc.
        element: <RootLayout />,
        errorElement: getErrorElement(),
        children: [
          // Public routes (lazy-loaded with Suspense)
          createRoute(
            '/',
            <LazyLoad>
              <Home />
            </LazyLoad>,
            false,
            false,
            undefined,
            true
          ),
          createRoute(
            '/About',
            <LazyLoad>
              <About />
            </LazyLoad>,
            false,
            false,
            undefined,
            true
          ),
          createRoute(
            '/Blog',
            <LazyLoad>
              <Blog />
            </LazyLoad>
          ),
          createRoute(
            '/Contact',
            <LazyLoad>
              <ContactUs />
            </LazyLoad>,
            false,
            false,
            undefined,
            true
          ),
          createRoute(
            '/Services',
            <LazyLoad>
              <Services />
            </LazyLoad>,
            false,
            false,
            undefined,
            true
          ),
          createRoute(
            '/Pricing',
            <LazyLoad>
              <PricingPage />
            </LazyLoad>,
            false,
            false,
            undefined,
            true
          ),
          createRoute(
            '/Terms',
            <LazyLoad>
              <TermsOfServicePage />
            </LazyLoad>,
            false,
            false,
            undefined,
            true
          ),
          createRoute(
            '/Privacy',
            <LazyLoad>
              <PrivacyPolicyPage />
            </LazyLoad>,
            false,
            false,
            undefined,
            true
          ),
          createRoute(
            '/Security',
            <LazyLoad>
              <SecurityPage />
            </LazyLoad>,
            false,
            false,
            undefined,
            true
          ),

          // Auth routes - no subscription needed (lazy-loaded)
          // HA-283: subscription routes are practitioner-only. The plan
          // picker / upgrade flow targets the practice buyer;
          // SubscriptionManagementPage doesn't role-check internally
          // either. Wrap each route's element in practitionerOnly() so
          // a Client typing /Subscription is redirected to /Dashboard
          // by RoleGuard. (HA-282 removed the Dashboard's upgrade-card
          // entry point; this closes the URL-typing path.)
          createRoute(
            '/Splash',
            practitionerOnly(
              <LazyLoad>
                <SplashPage />
              </LazyLoad>
            ),
            true
          ),
          createRoute(
            '/subscription/splash',
            practitionerOnly(
              <LazyLoad>
                <SplashPage />
              </LazyLoad>
            ),
            true
          ),
          createRoute(
            '/Subscription',
            practitionerOnly(
              <LazyLoad>
                <SubscriptionManagementPage />
              </LazyLoad>
            ),
            true
          ),
          createRoute(
            '/subscription/success',
            practitionerOnly(
              <LazyLoad>
                <SubscriptionSuccess />
              </LazyLoad>
            ),
            true
          ),

          // WHOOP OAuth callback - must be before authenticated layout routes
          // This route has skipShell=true so it doesn't get sidebar/header
          createRoute(
            '/settings/integrations/whoop/callback',
            <LazyLoad>
              <WhoopCallbackHandler />
            </LazyLoad>,
            true,
            false,
            undefined,
            true
          ),

          // Fitbit OAuth callback - mirrors the WHOOP callback route.
          // skipShell=true so it doesn't get the sidebar/header shell.
          createRoute(
            '/settings/integrations/fitbit/callback',
            <LazyLoad>
              <FitbitCallbackHandler />
            </LazyLoad>,
            true,
            false,
            undefined,
            true
          ),

          // Google Calendar OAuth callback - mirrors the WHOOP callback route.
          createRoute(
            '/settings/integrations/google/callback',
            <LazyLoad>
              <GoogleCallbackHandler />
            </LazyLoad>,
            true,
            false,
            undefined,
            true
          ),

          // Zoom OAuth callback - mirrors the WHOOP callback route.
          createRoute(
            '/settings/integrations/zoom/callback',
            <LazyLoad>
              <ZoomCallbackHandler />
            </LazyLoad>,
            true,
            false,
            undefined,
            true
          ),

          // Authenticated routes with sidebar layout
          // All routes nested under AuthenticatedLayout get the persistent sidebar
          {
            element: isAuthenticated() ? <AuthenticatedLayout /> : <Navigate to="/LogIn" />,
            errorElement: getErrorElement(),
            children: [
              createAuthRoute(
                '/AccountManagement',
                practitionerOnly(
                  <LazyLoad>
                    <ManageUsers />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/Calendar',
                <LicenseGuard feature="telehealth">
                  <LazyLoad>
                    <ScheduleComponent />
                  </LazyLoad>
                </LicenseGuard>
              ),
              createAuthRoute(
                '/ClientDashboard',
                // HA-372: client-only surface. ClientDashboard calls the patient
                // daily-logs / daily-summary / meals endpoints (clientId=email),
                // which 404/403 for non-Client roles. Mirror /health-metrics
                // (HA-351): redirect Practitioner/PracticeAdmin to /Dashboard
                // instead of firing a cascade of failing requests + console errors.
                clientOnly(
                  <LazyLoad>
                    <ClientDashboard />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/client-management',
                practitionerOnly(
                  <LazyLoad>
                    <ClientManagement />
                  </LazyLoad>
                )
              ),
              // HA-323: read-only client profile page. Linked from the
              // "Profile" buttons in ClientManagement, ClientListTable,
              // and ClientQuickViewDrawer (all already point at
              // /ClientProfile/{id}). Practitioner-only via RoleGuard
              // so Clients hitting the URL get redirected to /Dashboard
              // (HA-259 contract).
              createAuthRoute(
                '/ClientProfile/:clientId',
                practitionerOnly(
                  <LazyLoad>
                    <ClientProfilePage />
                  </LazyLoad>
                )
              ),
              // HA-446: the admin console - every practice, and one practice's people and plan.
              // No subscription guard: a SystemAdmin has no practice, so no plan to check.
              createAuthRoute(
                '/Admin',
                systemAdminOnly(
                  <LazyLoad>
                    <SystemAdminPracticesPage />
                  </LazyLoad>
                ),
                false
              ),
              createAuthRoute(
                '/Admin/Practices/:practiceId',
                systemAdminOnly(
                  <LazyLoad>
                    <SystemAdminPracticeDetailPage />
                  </LazyLoad>
                ),
                false
              ),
              // HA-446 stage 2: the audit viewer - admin actions, or any practice's log.
              createAuthRoute(
                '/Admin/Audit',
                systemAdminOnly(
                  <LazyLoad>
                    <SystemAdminAuditLogPage />
                  </LazyLoad>
                ),
                false
              ),
              // HA-411: read-only SystemAdmin account lookup tool.
              createAuthRoute(
                '/Admin/AccountLookup',
                systemAdminOnly(
                  <LazyLoad>
                    <SystemAdminAccountLookupPage />
                  </LazyLoad>
                ),
                false
              ),
              // HA-440: two-factor setup. Any signed-in role, with or without a
              // subscription (account security isn't a paid feature); SystemAdmins
              // are sent here when the API answers 403 MFA_REQUIRED.
              createAuthRoute(
                '/mfa/setup',
                <LazyLoad>
                  <MfaSetupPage />
                </LazyLoad>,
                false
              ),
              createAuthRoute(
                '/Dashboard',
                <LazyLoad>
                  <Dashboard />
                </LazyLoad>
              ),
              createAuthRoute(
                '/Settings',
                <LazyLoad>
                  <SettingsPage />
                </LazyLoad>
              ),
              createAuthRoute(
                '/settings',
                <LazyLoad>
                  <SettingsPage />
                </LazyLoad>
              ),
              createAuthRoute(
                '/Messaging',
                // HA-351 / HA-37: PracticeAdmin is excluded from messaging server-side
                // (MessageController [ExcludeRoles(PracticeAdmin)]); without this guard an
                // admin lands on a guaranteed-403 page. Allow Client + Practitioner +
                // SystemAdmin; redirect PracticeAdmin to /Dashboard. (cf. HA-BUG-010.)
                <RoleGuard allow={[UserRole.Client, UserRole.Practitioner, UserRole.SystemAdmin]}>
                  <LazyLoad>
                    <Messaging />
                  </LazyLoad>
                </RoleGuard>
              ),
              // HA-286: Onboarding wizard is practitioner-only.
              // The Practice Setup flow targets the practice
              // buyer (asks for team size, practice website,
              // greets the user as "Practice Administrator").
              // OnboardingFlow doesn't role-check internally;
              // wrap in RoleGuard via the existing
              // practitionerOnly helper so a Client typing
              // /Onboarding is redirected to /Dashboard.
              // Dashboard.tsx auto-redirect is already gated on
              // isPracticeAdmin; this closes the URL-typing path.
              createAuthRoute(
                '/Onboarding',
                practitionerOnly(
                  <LazyLoad>
                    <OnboardingFlow />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/Scheduling',
                <LicenseGuard feature="telehealth">
                  <LazyLoad>
                    <SchedulerComponent />
                  </LazyLoad>
                </LicenseGuard>
              ),

              // Training Programs routes (HA-225)
              createAuthRoute(
                '/training/programs',
                practitionerOnly(
                  <LazyLoad>
                    <TrainingPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/training/programs/new',
                practitionerOnly(
                  <LazyLoad>
                    <TrainingPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/training/programs/:programId',
                practitionerOnly(
                  <LazyLoad>
                    <TrainingPage />
                  </LazyLoad>
                )
              ),

              // Forms routes
              createAuthRoute(
                '/forms',
                practitionerOnly(
                  <LazyLoad>
                    <FormsPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/forms/new',
                practitionerOnly(
                  <LazyLoad>
                    <FormsPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/forms/new/ai',
                practitionerOnly(
                  <LazyLoad>
                    <FormsPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/forms/:formId',
                practitionerOnly(
                  <LazyLoad>
                    <FormsPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/forms/:formId/analytics',
                practitionerOnly(
                  <LazyLoad>
                    <FormsPage />
                  </LazyLoad>
                )
              ),

              // HA-260: patient-facing forms list. Intentionally NOT wrapped
              // in practitionerOnly — this is the Client surface. The
              // practitioner author UI lives under /forms (gated).
              createAuthRoute(
                '/my-forms',
                <LazyLoad>
                  <MyFormsPage />
                </LazyLoad>
              ),
              // HA-269: patient form fill-out + draft + submit. Reuses
              // /api/v1/me/forms/assignments/{id} (HA-269 backend).
              createAuthRoute(
                '/my-forms/:assignmentId',
                <LazyLoad>
                  <MyFormDetailPage />
                </LazyLoad>
              ),

              // HA-237: Orphaned component routes
              createAuthRoute(
                '/community',
                practitionerOnly(
                  <LazyLoad>
                    <Forum />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/exercises',
                practitionerOnly(
                  <LazyLoad>
                    <ExerciseBrowser />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/exercises/mine',
                practitionerOnly(
                  <LazyLoad>
                    <MyExercises />
                  </LazyLoad>
                )
              ),

              // Recipes routes
              createAuthRoute(
                '/recipes',
                practitionerOnly(
                  <LazyLoad>
                    <RecipesPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/recipes/new',
                practitionerOnly(
                  <LazyLoad>
                    <RecipesPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/recipes/:recipeId',
                practitionerOnly(
                  <LazyLoad>
                    <RecipesPage />
                  </LazyLoad>
                )
              ),

              // Meal Plans routes
              createAuthRoute(
                '/meal-plans',
                practitionerOnly(
                  <LazyLoad>
                    <MealPlansPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/meal-plans/new',
                practitionerOnly(
                  <LazyLoad>
                    <MealPlansPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/meal-plans/new/ai',
                practitionerOnly(
                  <LazyLoad>
                    <MealPlansPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/meal-plans/:mealPlanId',
                practitionerOnly(
                  <LazyLoad>
                    <MealPlansPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/meal-plans/:mealPlanId/shopping',
                practitionerOnly(
                  <LazyLoad>
                    <MealPlansPage />
                  </LazyLoad>
                )
              ),

              // HA-261: patient-facing meal plans list. Intentionally NOT
              // wrapped in practitionerOnly — Client surface. The
              // practitioner author UI lives under /meal-plans (gated).
              createAuthRoute(
                '/my-meal-plans',
                <LazyLoad>
                  <MyMealPlansPage />
                </LazyLoad>
              ),
              // HA-270: patient meal plan detail (day breakdown +
              // shopping list + recipe drill-down).
              createAuthRoute(
                '/my-meal-plans/:assignmentId',
                <LazyLoad>
                  <MyMealPlanDetailPage />
                </LazyLoad>
              ),

              // HA-262: patient-facing programs list. Ungated — Client
              // surface. The practitioner author UI lives under /training
              // (gated by HA-259). Detail route is /my-programs/:id.
              createAuthRoute(
                '/my-programs',
                <LazyLoad>
                  <MyProgramsPage />
                </LazyLoad>
              ),
              createAuthRoute(
                '/my-programs/:assignmentId',
                <LazyLoad>
                  <MyProgramDetailPage />
                </LazyLoad>
              ),

              // HA-264: patient-facing standalone Health Metrics page.
              // Real chart history + log CRUD ship with HA-272 once
              // HA-265's daily-logs pipeline is healthy.
              // HA-351: Client-gated via clientOnly — HealthMetricsPage
              // calls the patient daily-logs endpoints (clientId=email),
              // whose !isClient path 403/404s for Practitioner/PracticeAdmin.
              createAuthRoute(
                '/health-metrics',
                clientOnly(
                  <LazyLoad>
                    <HealthMetricsPage />
                  </LazyLoad>
                )
              ),

              // HA-266: patient-facing standalone Appointments page.
              // Reuses /api/v1/telehealth/my-sessions (HA-263). Real
              // self-scheduling, practitioner profile, reschedule/
              // cancel, and in-person appointments ship with HA-273.
              createAuthRoute(
                '/appointments',
                <LazyLoad>
                  <PatientAppointmentsPage />
                </LazyLoad>
              ),

              createAuthRoute(
                '/resources',
                practitionerOnly(
                  <LazyLoad>
                    <ResourceLibrary />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/templates',
                practitionerOnly(
                  <LazyLoad>
                    <TemplateLibrary />
                  </LazyLoad>
                )
              ),

              // Reports routes
              createAuthRoute(
                '/reports',
                practitionerOnly(
                  <LazyLoad>
                    <ReportsPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/reports/new',
                practitionerOnly(
                  <LazyLoad>
                    <ReportsPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/reports/generated',
                practitionerOnly(
                  <LazyLoad>
                    <ReportsPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/reports/generate',
                practitionerOnly(
                  <LazyLoad>
                    <ReportsPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/reports/generate/:templateId',
                practitionerOnly(
                  <LazyLoad>
                    <ReportsPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/reports/view/:clientId/:reportId',
                practitionerOnly(
                  <LazyLoad>
                    <ReportsPage />
                  </LazyLoad>
                )
              ),
              createAuthRoute(
                '/reports/:templateId',
                practitionerOnly(
                  <LazyLoad>
                    <ReportsPage />
                  </LazyLoad>
                )
              ),

              // Group Sessions route
              createAuthRoute(
                '/group-sessions',
                practitionerOnly(
                  <LazyLoad>
                    <GroupSessionsPage />
                  </LazyLoad>
                )
              ),

              // Telehealth route
              createAuthRoute(
                '/telehealth',
                <LazyLoad>
                  <TelehealthPage />
                </LazyLoad>
              ),

              // Broadcasts route
              createAuthRoute(
                '/broadcasts',
                practitionerOnly(
                  <LazyLoad>
                    <BroadcastsPage />
                  </LazyLoad>
                )
              ),

              // Team Calendar route
              createAuthRoute(
                '/team-calendar',
                practitionerOnly(
                  <LazyLoad>
                    <TeamCalendarPage />
                  </LazyLoad>
                )
              ),
            ],
          },

          // /help route redirects to Dashboard - Help Center is now a global drawer
          {
            path: '/help',
            element: <Navigate to="/Dashboard?view=help" replace />,
          },

          // Special routes
          {
            path: '/SignOut',
            element: <SignOut />,
          },
          {
            path: '/LogIn',
            element: isAuthenticated() ? <Navigate to="/Dashboard" /> : <SignUpPage />,
          },
          {
            // Case-insensitive redirect for /login - preserves query params (HA-226)
            path: '/login',
            element: <LoginRedirect />,
          },
          {
            path: '/SignUp',
            element: isAuthenticated() ? <Navigate to="/Dashboard" /> : <SignUpPage />,
          },
          {
            path: '/ClientManagement',
            element: <Navigate to="/client-management" replace />,
          },
          {
            // HA-321: legacy /Schedule → /Scheduling redirect. Catches
            // stale bookmarks like /Schedule?clientIds=...&mode=group
            // that pre-date the link rename in clientManagement components.
            path: '/Schedule',
            element: <ScheduleRedirect />,
          },
          {
            // HA-326: legacy /Broadcast → /broadcasts redirect. Catches
            // stale bookmarks like /Broadcast?clientIds=... that pre-date
            // the link rename in clientManagement components.
            path: '/Broadcast',
            element: <BroadcastRedirect />,
          },
        ],
      },
    ],
    {}
  );

  return (
    <AppTheme themeEnum={getCurrentTheme}>
      <AuthProvider>
        <PracticeProvider>
          <LicenseProvider>
            <ActivePracticeProvider>
              <BrandingProvider>
                <RouterProvider router={router} />
                <SessionExpiryModal />
                <CookieConsentBanner />
              </BrandingProvider>
            </ActivePracticeProvider>
          </LicenseProvider>
        </PracticeProvider>
      </AuthProvider>
    </AppTheme>
  );
};

export default App;
